// =====================================================
// 🚀 MAIN EXECUTION - Run immediately and on DOMContentLoaded
// =====================================================

(function () {
  const _cartForkliftLog = typeof window.createLogger === 'function'
    ? window.createLogger('CartForklift')
    : { log: () => {}, warn: () => {}, error: () => {} };

  _cartForkliftLog.log('IIFE executing, window.subscribe:', typeof window.subscribe, 'window.PUB_SUB_EVENTS:', typeof window.PUB_SUB_EVENTS);

  // Track previous weight state to detect threshold crossings
  let previousWeightAboveThreshold = false;
  const WEIGHT_THRESHOLD = 30; // kg
  let isInitialized = false;
  let retryCount = 0;
  const MAX_RETRIES = 5; // Max 5 seconds (5 * 1000ms)
  let weightUnit = null; // Store weight unit in closure scope

  // =====================================================
  // ⚙️ CORE CART FUNCTIONS
  // =====================================================

  /** Calculates and updates total cart weight */
  function updateCartWeight() {
    if (!weightUnit) {
      _cartForkliftLog.warn('weightUnit not available yet');
      return;
    }

    let totalWeight = 0;

    const weightInputs = document.querySelectorAll('.quantity-input-field');
    _cartForkliftLog.log('Found', weightInputs.length, 'quantity input fields');

    weightInputs.forEach((input) => {
      const quantity = parseInt(input.value, 10) || 0;
      const weightPerVariant = parseFloat(input.getAttribute('data-weight')) || 0;
      totalWeight += quantity * weightPerVariant;
      _cartForkliftLog.log('Item qty:', quantity, 'weight:', weightPerVariant, 'subtotal:', quantity * weightPerVariant);
    });

    _cartForkliftLog.log('Total weight before conversion:', totalWeight, 'Unit:', weightUnit);
    if (weightUnit !== 'kg') totalWeight /= 1000;
    _cartForkliftLog.log('Total weight in kg:', totalWeight);

    // Check if we crossed the threshold
    const currentWeightAboveThreshold = totalWeight > WEIGHT_THRESHOLD;
    const crossedThreshold = previousWeightAboveThreshold !== currentWeightAboveThreshold;

    _cartForkliftLog.log(
      'Current weight above threshold:', currentWeightAboveThreshold,
      'Previous:', previousWeightAboveThreshold,
      'Crossed:', crossedThreshold
    );

    if (crossedThreshold) {
      _cartForkliftLog.log(`Weight threshold crossed! Previous: ${previousWeightAboveThreshold}, Current: ${currentWeightAboveThreshold}`);
      triggerShippingRefresh();
    }

    // Update previous state
    previousWeightAboveThreshold = currentWeightAboveThreshold;

    handleForkliftDisplay(totalWeight);
  }

  /** Triggers shipping rate refresh by simulating form submission */
  function triggerShippingRefresh() {
    try {
      const shippingCalculator = document.querySelector('shipping-calculator');
      if (!shippingCalculator) {
        _cartForkliftLog.warn('Shipping calculator not found');
        return;
      }

      const postcodeInput = shippingCalculator.querySelector('.postcode');
      if (!postcodeInput) {
        _cartForkliftLog.warn('Postcode input not found');
        return;
      }

      const postcodeValue = postcodeInput.value.trim();
      if (!postcodeValue) {
        _cartForkliftLog.warn('Postcode field is empty, skipping shipping rate refresh');
        return;
      }

      _cartForkliftLog.log('Triggering shipping rate refresh...');

      // Only trigger form submission to refresh rates - don't dispatch country change
      // Country hasn't changed, we just need updated rates based on new weight
      setTimeout(() => {
        const submitButton = shippingCalculator.querySelector('.button');
        if (submitButton) {
          submitButton.click();
          _cartForkliftLog.log('Shipping rates refresh requested');
        }
      }, 100);
    } catch (err) {
      _cartForkliftLog.error('Error triggering shipping refresh:', err);
    }
  }

  /** Reloads page to update cart totals */
  function updateCartTotals() {
    // location.reload();
  }

  /** Handles forklift question display logic */
  function handleForkliftDisplay(totalWeight) {
    _cartForkliftLog.log('handleForkliftDisplay called with weight:', totalWeight);

    const forkliftQuestionDiv = document.querySelector('.cart-details');
    if (!forkliftQuestionDiv) {
      _cartForkliftLog.warn('.cart-details element not found');
      return;
    }

    _cartForkliftLog.log('Found .cart-details element');

    if (totalWeight > 30) {
      _cartForkliftLog.log('Weight > 30, showing forklift message');
      forkliftQuestionDiv.style.display = 'block';
      if (typeof updateCheckoutButton === 'function') {
        updateCheckoutButton(true);
      } else {
        _cartForkliftLog.warn('updateCheckoutButton function not found');
      }
      resetForkliftRadios();
    } else {
      _cartForkliftLog.log('Weight <= 30, hiding forklift message');
      forkliftQuestionDiv.style.display = 'none';
      if (typeof updateCheckoutButton === 'function') {
        updateCheckoutButton(false);
      } else {
        _cartForkliftLog.warn('updateCheckoutButton function not found');
      }
    }
  }

  /** Unchecks forklift yes/no options */
  function resetForkliftRadios() {
    _cartForkliftLog.log('Resetting forklift radio buttons');
    const radioYes = document.getElementById('forklift-yes');
    const radioNo = document.getElementById('forklift-no');
    if (radioYes && radioNo) {
      radioYes.checked = false;
      radioNo.checked = false;
      _cartForkliftLog.log('Radio buttons reset');
    } else {
      _cartForkliftLog.warn('Radio buttons not found');
    }
  }

  // =====================================================
  // 📦 CART EVENT HANDLERS
  // =====================================================

  /** Setup listeners for cart events */
  function registerCartEvents() {
    if (isInitialized) {
      _cartForkliftLog.log('Already initialized, skipping registration');
      return;
    }

    _cartForkliftLog.log('Registering cart event listeners');

    document.addEventListener('cart:item:removed', () => {
      _cartForkliftLog.log('cart:item:removed event received');
      setTimeout(updateCartWeight, 700);
    });

    document.body.addEventListener('click', (event) => {
      const isQuantityButton =
        event.target.classList.contains('quantity-increase-button') ||
        event.target.classList.contains('quantity-decrease-button');
      if (isQuantityButton) {
        _cartForkliftLog.log('Quantity button clicked');
        setTimeout(updateCartTotals, 1000);
      }
    });

    // Listen for cart updates (quantity changes via PubSub events)
    // This is the SINGLE SOURCE OF TRUTH for cart changes
    if (typeof window.subscribe === 'function' && typeof window.PUB_SUB_EVENTS !== 'undefined') {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
        try {
          _cartForkliftLog.log('Cart updated event received, recalculating weight');
          _cartForkliftLog.log('Cart data:', event.cartData);

          // Wait for DOM to update with new quantities from Shopify's response
          setTimeout(() => {
            updateCartWeight();

            // Dispatch custom event to notify other calculators (single source of truth)
            window.dispatchEvent(
              new CustomEvent('cart-forklift-processed', {
                detail: {
                  cartData: event.cartData,
                  source: 'cartUpdate'
                }
              })
            );
            _cartForkliftLog.log('Dispatched cart-forklift-processed event');
          }, 300);
        } catch (err) {
          _cartForkliftLog.warn('Cart update event error:', err);
        }
      });

      window.subscribe(window.PUB_SUB_EVENTS.cartItemRemove, (event) => {
        try {
          _cartForkliftLog.log('Cart item removed event received, recalculating');

          setTimeout(() => {
            updateCartWeight();

            // Dispatch custom event to notify other calculators
            window.dispatchEvent(
              new CustomEvent('cart-forklift-processed', {
                detail: {
                  cartData: event.cart,
                  source: 'cartItemRemove'
                }
              })
            );
            _cartForkliftLog.log('Dispatched cart-forklift-processed event after item removal');
          }, 300);
        } catch (err) {
          _cartForkliftLog.warn('Cart item remove event error:', err);
        }
      });

      _cartForkliftLog.log('Subscribed to PubSub cart update events (SINGLE SOURCE OF TRUTH)');
      isInitialized = true;
    } else {
      _cartForkliftLog.warn('PubSub not available, using fallback click handlers');
      _cartForkliftLog.warn('subscribe:', typeof window.subscribe, 'PUB_SUB_EVENTS:', typeof window.PUB_SUB_EVENTS);
    }
  }

  // =====================================================
  // 🚀 INITIALIZE
  // =====================================================
  function init() {
    _cartForkliftLog.log('Init called (attempt', retryCount + 1, '/', MAX_RETRIES, ')');
    _cartForkliftLog.log('window.CartGlobalsForklift:', window.CartGlobalsForklift);

    const weightUnit = window.CartGlobalsForklift?.weightUnit;
    if (!weightUnit) {
      retryCount++;
      if (retryCount >= MAX_RETRIES) {
        _cartForkliftLog.error('Max retries reached. CartGlobalsForklift not found. Using fallback.');
        // Use fallback - assume grams
        const fallbackUnit = 'g';
        _cartForkliftLog.log('Using fallback weightUnit:', fallbackUnit);
        initializeWithWeightUnit(fallbackUnit);
        return;
      }
      _cartForkliftLog.warn('weightUnit not available, retrying in 100ms... (attempt', retryCount, '/', MAX_RETRIES, ')');
      setTimeout(init, 100);
      return;
    }

    _cartForkliftLog.log('Found weightUnit:', weightUnit);
    retryCount = 0; // Reset retry count
    initializeWithWeightUnit(weightUnit);
  }

  function initializeWithWeightUnit(weightUnitParam) {
    _cartForkliftLog.log('Initializing with weightUnit:', weightUnitParam);

    // Set the closure weightUnit variable
    weightUnit = weightUnitParam;

    // Initialize previous weight state
    let totalWeight = 0;
    const inputs = document.querySelectorAll('.quantity-input-field');
    _cartForkliftLog.log('Found', inputs.length, 'quantity input fields during init');

    inputs.forEach((input) => {
      const quantity = parseInt(input.value, 10) || 0;
      const weightPerVariant = parseFloat(input.getAttribute('data-weight')) || 0;
      totalWeight += quantity * weightPerVariant;
    });
    if (weightUnit !== 'kg') totalWeight /= 1000;
    previousWeightAboveThreshold = totalWeight > WEIGHT_THRESHOLD;
    _cartForkliftLog.log('Initial weight:', totalWeight, 'kg, Above threshold:', previousWeightAboveThreshold);

    updateCartWeight();
    registerCartEvents();
  }

  // Run initialization - wait for DOMContentLoaded to ensure inline scripts are executed
  _cartForkliftLog.log('Script loaded, document.readyState:', document.readyState);

  if (document.readyState === 'loading') {
    _cartForkliftLog.log('Waiting for DOMContentLoaded...');
    document.addEventListener('DOMContentLoaded', () => {
      _cartForkliftLog.log('DOMContentLoaded fired');
      init();
    });
  } else {
    _cartForkliftLog.log('DOM already loaded, initializing immediately');
    init();
  }

  // Also listen for section refreshes (for SPA-style navigation)
  if (typeof window.subscribe === 'function' && typeof window.PUB_SUB_EVENTS !== 'undefined') {
    window.subscribe(window.PUB_SUB_EVENTS.sectionRefreshed, () => {
      _cartForkliftLog.log('Section refreshed, re-initializing');
      isInitialized = false;
      retryCount = 0; // Reset retry counter
      init();
    });
  }
})();
